import React from "react"
import Avatar from "@mui/material/Avatar"
import { Mail, Phone } from "lucide-mui"
import { Divider, Skeleton } from "@mui/material"
import { useQuery } from "@apollo/client/react"
import { GetCustomerDocument } from "@/graphql/graphql"
import i18n from "@/i18n"

const STYLES = {
  box: "shadow rounded overflow-hidden border border-solid border-gray-300 bg-white",
  header: "flex items-center py-1.5 px-3 bg-gray-100",
  avatar: "w-6 h-6 mr-2",
  name: "text-sm font-semibold text-gray-700 m-0 p-0",
  contactsContainer: "px-3 py-3 pb-2",
  contactItem: "flex items-center mb-1 text-xs text-gray-900",
  contactIcon: "mr-1.5 text-gray-600",
  noContactDetails: "flex items-center mb-1 text-xs text-gray-400",
  dividerInBox: "mx-2.5",
  statsContainer: "px-3 py-2 flex justify-center gap-3 text-xs text-gray-600",
  statItem: "whitespace-nowrap",
}

interface CustomerBoxProps {
  customerId?: string | null
  customerContact: {
    name: string
    email: string
    phoneFormatted: string
    avatarUrl: string
  }
  className?: string
}

const CustomerBox: React.FC<CustomerBoxProps> = ({ customerId, customerContact, className }) => {
  const { data, loading } = useQuery(GetCustomerDocument, {
    variables: { id: customerId },
    skip: !customerId,
  })

  const stats = data?.business?.customer?.stats?.reservations

  return (
    <div className={`${STYLES.box} ${className}`} data-testid="panel-reservations-customer-box">
      <div className={STYLES.header}>
        <Avatar src={customerContact.avatarUrl} className={STYLES.avatar} />
        <h2 className={STYLES.name}>{customerContact.name}</h2>
      </div>

      <div className={STYLES.contactsContainer}>
        {!customerContact.phoneFormatted && !customerContact.email ? (
          <span className={STYLES.noContactDetails}>{i18n.t("app.customer_box.no_contact_details")}</span>
        ) : (
          <>
            {!!customerContact.phoneFormatted && (
              <div className={STYLES.contactItem}>
                <Phone style={{ width: "10px", height: "10px" }} className={STYLES.contactIcon} />
                <span>{customerContact.phoneFormatted}</span>
              </div>
            )}
            {!!customerContact.email && (
              <div className={STYLES.contactItem}>
                <Mail style={{ width: "10px", height: "10px" }} className={STYLES.contactIcon} />
                <span>{customerContact.email}</span>
              </div>
            )}
          </>
        )}
      </div>

      <Divider className={STYLES.dividerInBox} />

      <div className={STYLES.statsContainer}>
        {loading ? (
          <>
            <Skeleton variant="text" width={80} height={16} />
            <Skeleton variant="text" width={60} height={16} />
            <Skeleton variant="text" width={70} height={16} />
            <Skeleton variant="text" width={65} height={16} />
          </>
        ) : stats ? (
          <>
            <span className={STYLES.statItem}>Reservations: {stats.total}</span>
            <span className={STYLES.statItem}>Visits: {stats.seated}</span>
            <span className={STYLES.statItem}>Cancelled: {stats.cancelled}</span>
            <span className={STYLES.statItem}>No-show: {stats.noShow}</span>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default CustomerBox
